import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Zap,
  Plus,
  Minus,
  Trash2,
  Save,
  X,
  User,
  Phone,
  Banknote,
  CreditCard,
  Smartphone,
  ArrowRight,
  Package,
  Search,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Eye,
  Pencil,
  XCircle,
  Loader2,
  ShoppingCart,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";


const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const QuickPOS = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [staffList, setStaffList] = useState([]);
  const [referredByStaffId, setReferredByStaffId] = useState("");

  // Products & pagination
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [productsLoading, setProductsLoading] = useState(false);
  const PAGE_SIZE = 20;

  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerGSTIN, setCustomerGSTIN] = useState("");
  const [creditNotes, setCreditNotes] = useState([]);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditSourceOrder, setCreditSourceOrder] = useState(null);

  const [paymentBreakdown, setPaymentBreakdown] = useState({
    cash: "",
    card: "",
    upi: "",
  });

  const [saving, setSaving] = useState(false);
  const [barcodeBuffer, setBarcodeBuffer] = useState("");
  const lastKeyTime = useRef(0);

  // ── My Orders panel state ──────────────────────────────────────────────────
  const [showMyOrders, setShowMyOrders] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [loadingOrderId, setLoadingOrderId] = useState(null);
  // Tracks the temp order that was loaded into the cart for editing.
  // When the staff saves a new order, the original is silently deleted.
  const [loadedFromOrderId, setLoadedFromOrderId] = useState(null);


  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchQuery, activeCategory]);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await axios.get(`${API_URL}/staff/referrals`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const all = Array.isArray(res.data) ? res.data : [];
        setStaffList(
          all.filter((u) => ["staff", "billing", "manager"].includes(u.role)),
        );
      } catch (err) {
        console.error("Failed to fetch staff:", err);
      }
    };
    fetchStaff();
  }, [token]);

  const [returningCustomerInfo, setReturningCustomerInfo] = useState(null);
  const [lookingUpCustomer, setLookingUpCustomer] = useState(false);

  useEffect(() => {
    const phone = customerPhone.replace(/\D/g, "");
    if (phone.length < 10) {
      setCreditNotes([]);
      setSelectedCreditNote(null);
      setCreditAmount("");
      setReturningCustomerInfo(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLookingUpCustomer(true);
      try {
        const [creditRes, lookupRes] = await Promise.all([
          axios.get(`${API_URL}/credit-notes`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { customer_phone: phone },
          }),
          axios.get(`${API_URL}/customers/lookup`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { phone },
          }),
        ]);
        setCreditNotes((creditRes.data || []).filter((note) =>
          ["ACTIVE", "PARTIALLY_USED"].includes(String(note.status || "").toUpperCase()) &&
          Number(note.remaining_credit || 0) > 0,
        ));
        if (lookupRes.data?.found) {
          setReturningCustomerInfo(lookupRes.data);
          setCustomerName((prev) => (!prev.trim() ? lookupRes.data.customer_name || "" : prev));
          if (lookupRes.data.customer_address) {
            setCustomerAddress((prev) => (!prev.trim() ? lookupRes.data.customer_address : prev));
          }
          if (lookupRes.data.customer_gstin) {
            setCustomerGSTIN((prev) => (!prev.trim() ? lookupRes.data.customer_gstin : prev));
          }
          toast.success(`Customer found: ${lookupRes.data.customer_name} (${lookupRes.data.total_orders} past orders)`);
        } else {
          setReturningCustomerInfo(null);
        }
      } catch (err) {
        console.error("Failed to fetch customer data:", err);
      } finally {
        setLookingUpCustomer(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [customerPhone, token]);

  useEffect(() => {
    if (!selectedCreditNote?.original_order_id) {
      setCreditSourceOrder(null);
      return;
    }
    axios.get(`${API_URL}/orders/${selectedCreditNote.original_order_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      const order = res.data;
      setCreditSourceOrder(order);
      setCustomerName(order.customer_name || selectedCreditNote.customer_name || "");
      setCustomerPhone(order.customer_phone || selectedCreditNote.customer_phone || "");
      setCustomerAddress(order.customer_address || "");
      setCustomerGSTIN(order.customer_gstin || "");
    }).catch(() => setCreditSourceOrder(null));
  }, [selectedCreditNote, token]);

  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: currentPage, limit: PAGE_SIZE, search: searchQuery },
      });
      setProducts(res.data.products || []);
      setCurrentPage(res.data.pagination.page || currentPage);
      setTotalPages(res.data.pagination.pages || 1);
      setTotalProducts(res.data.pagination.total || 0);
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setProductsLoading(false);
    }
  };

  // Categories
  const categories = React.useMemo(() => {
    const cats = [
      ...new Set(products.map((p) => p.category).filter(Boolean)),
    ].sort();
    return ["all", ...cats];
  }, [products]);

  // Filtered products
  const filteredProducts = React.useMemo(
    () =>
      products.filter((p) => {
        const matchCat =
          activeCategory === "all" || p.category === activeCategory;
        return matchCat;
      }),
    [products, activeCategory],
  );

  // Barcode scanner
  useEffect(() => {
    const handleKeyDown = (e) => {
      const now = Date.now();
      const gap = now - lastKeyTime.current;
      lastKeyTime.current = now;

      if (e.key === "Enter") {
        const code = barcodeBuffer.trim();
        if (code.length >= 4) {
          e.preventDefault();
          processBarcode(code);
          setBarcodeBuffer("");
        }
        return;
      }

      if (e.key.length === 1) {
        setBarcodeBuffer((prev) => (gap > 500 ? e.key : prev + e.key));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [barcodeBuffer]);

  const processBarcode = async (code) => {
    try {
      const res = await axios.get(`${API_URL}/products/barcode/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      addToCart(res.data);
      toast.success(`Added: ${res.data.name}`);
    } catch {
      toast.error(`Product not found: ${code}`);
    }
  };

  const calculateDiscountedPrice = (basePrice, discount, discountType) => {
    if (!discount || discount <= 0) return basePrice;
    const raw =
      discountType === "percentage"
        ? basePrice - (basePrice * discount) / 100
        : basePrice - discount;
    return Math.round(raw * 100) / 100;
  };

  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        if (existing.quantity >= product.stock_quantity) {
          toast.error(`Only ${product.stock_quantity} available in stock`);
          return prev;
        }
        return prev.map((i) =>
          i.product_id === product.id
            ? {
                ...i,
                quantity: i.quantity + 1,
                total: i.price * (i.quantity + 1),
              }
            : i,
        );
      }
      if (product.stock_quantity === 0) {
        toast.error("Product out of stock");
        return prev;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          discount: Number(product.discount || 0),
          discount_type: product.discount_type || "percentage",
          // Categories uses 18% when a product has no explicit GST rate.
          // Preserve an explicit 0% rate, but keep Quick POS consistent.
          gst_rate: product.gst_rate != null ? product.gst_rate : 18,
          price: calculateDiscountedPrice(
            product.selling_price,
            product.discount,
            product.discount_type,
          ),
          total: calculateDiscountedPrice(
            product.selling_price,
            product.discount,
            product.discount_type,
          ),
        },
      ];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.product_id !== productId) return item;
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          const product = products.find((p) => p.id === productId);
          if (product && newQty > product.stock_quantity) {
            toast.error(`Only ${product.stock_quantity} available in stock`);
            return item;
          }
          return {
            ...item,
            quantity: newQty,
            total: item.price * newQty,
          };
        })
        .filter(Boolean),
    );
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((i) => i.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const appliedCredit = selectedCreditNote
    ? roundMoney(Math.min(Number(creditAmount) || 0, Number(selectedCreditNote.remaining_credit) || 0, subtotal))
    : 0;
  const amountPayable = Math.max(0, subtotal - appliedCredit);
  const amountPayableToCustomer = Math.max(0, appliedCredit - subtotal);
  const amountReceived =
    parseFloat(paymentBreakdown.cash || 0) +
    parseFloat(paymentBreakdown.card || 0) +
    parseFloat(paymentBreakdown.upi || 0);

  // ── My Orders: fetch, load-into-cart, cancel ───────────────────────────────
  const fetchMyOrders = async () => {
    setOrdersLoading(true);
    try {
      const res = await axios.get(`${API_URL}/temporary-orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "pending" },
        // Backend automatically scopes to current user's orders for staff/billing roles
      });
      setMyOrders(res.data || []);
    } catch (err) {
      toast.error("Failed to load your orders");
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleOpenMyOrders = () => {
    setShowMyOrders(true);
    fetchMyOrders();
  };

  const handleLoadIntoCart = async (order) => {
    setLoadingOrderId(order.id);
    try {
      // Restore cart items
      const cartItems = (order.items || []).map((item) => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        price: roundMoney(item.price),
        total: roundMoney(item.total || item.price * item.quantity),
        discount: Number(item.discount || 0),
        discount_type: item.discount_type || "percentage",
        gst_rate: item.gst_rate != null ? item.gst_rate : 18,
      }));
      setCart(cartItems);

      // Restore customer details
      setCustomerName(order.customer_name || "");
      setCustomerPhone(order.customer_phone || "");
      setCustomerAddress(order.customer_address || "");
      setCustomerGSTIN(order.customer_gstin || "");
      setReferredByStaffId(order.referred_by_staff_id || "");

      // Restore payment breakdown
      const pb = order.payment_breakdown || {};
      setPaymentBreakdown({
        cash: pb.cash ? String(pb.cash) : "",
        card: pb.card ? String(pb.card) : "",
        upi: pb.upi ? String(pb.upi) : "",
      });

      // Remember which order we loaded — will be deleted after a successful re-save
      setLoadedFromOrderId(order.id);

      setShowMyOrders(false);
      toast.success(`Order ${order.temp_order_number} loaded into cart — edit and re-save`);
    } catch (err) {
      toast.error("Failed to load order into cart");
    } finally {
      setLoadingOrderId(null);
    }
  };

  const handleCancelOrder = async (order) => {
    if (!window.confirm(`Cancel order ${order.temp_order_number}? This cannot be undone.`)) return;
    setCancellingId(order.id);
    try {
      await axios.put(
        `${API_URL}/temporary-orders/${order.id}`,
        { status: "cancelled" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMyOrders((prev) => prev.filter((o) => o.id !== order.id));
      toast.success(`Order ${order.temp_order_number} cancelled`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to cancel order");
    } finally {
      setCancellingId(null);
    }
  };

  const handleSaveTemporary = async () => {

    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    if (!customerName.trim()) {
      toast.error("Customer name required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        items: cart.map((item) => ({
          ...item,
          discount: Number(item.discount || 0),
          discount_type: item.discount_type || "percentage",
        })),
        subtotal,
        total: roundMoney(amountPayable),
        credit_note_used: appliedCredit,
        amount_payable: roundMoney(amountPayable),
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_address: customerAddress,
        customer_gstin: customerGSTIN,
        referred_by_staff_id: referredByStaffId || "",
        referred_by_staff_id: referredByStaffId,
        payment_breakdown: {
          cash: parseFloat(paymentBreakdown.cash || 0),
          card: parseFloat(paymentBreakdown.card || 0),
          upi: parseFloat(paymentBreakdown.upi || 0),
        },
        amount_received: amountReceived,
        credit_note_allocations: selectedCreditNote && appliedCredit > 0
          ? [{ credit_note_id: selectedCreditNote.id, amount: roundMoney(appliedCredit) }]
          : [],
      };

      const res = await axios.post(`${API_URL}/temporary-orders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(`Saved as ${res.data.temp_order_number}`);

      // If this was an edited re-save of a previous temp order, silently delete the original
      if (loadedFromOrderId) {
        try {
          await axios.put(
            `${API_URL}/temporary-orders/${loadedFromOrderId}`,
            { status: "cancelled" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch {
          // Non-critical: original order deletion failed — ignore silently
        }
        setLoadedFromOrderId(null);
      }

      // Clear form
      setCart([]);
      setCustomerName("");
      setCustomerPhone("");
      setCustomerAddress("");
      setCustomerGSTIN("");
      setCreditNotes([]);
      setSelectedCreditNote(null);
      setCreditAmount("");
      setReferredByStaffId("");
      setPaymentBreakdown({ cash: "", card: "", upi: "" });

      // Optional: navigate to main POS
      //   setTimeout(() => {
      //     navigate("/pos");
      //   }, 1000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 || /already used|changed/i.test(String(detail || ""))) {
        setCreditNotes((prev) => prev.filter((note) => note.id !== selectedCreditNote?.id));
        setSelectedCreditNote(null);
        setCreditAmount("");
        toast.error("This credit note was already used. The list has been refreshed.");
      } else {
        toast.error(detail || "Failed to save temporary order");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-stone-50 p-4">
      {/* ── My Orders Slide-over Drawer ─────────────────────────────────────── */}
      <AnimatePresence>
        {showMyOrders && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMyOrders(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            />
            {/* Drawer */}
            <motion.div
              key="drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-amber-500 to-orange-500">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-white/20 rounded-lg">
                    <ClipboardList size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-white">My Temporary Orders</h2>
                    <p className="text-xs text-amber-100">Only your own pending orders are shown</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchMyOrders}
                    disabled={ordersLoading}
                    className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw size={15} className={ordersLoading ? "animate-spin" : ""} />
                  </button>
                  <button
                    onClick={() => setShowMyOrders(false)}
                    className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Drawer Body */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {ordersLoading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Loader2 size={32} className="text-amber-500 animate-spin" />
                    <p className="text-sm text-stone-500">Loading your orders...</p>
                  </div>
                ) : myOrders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                    <div className="p-4 bg-stone-100 rounded-2xl">
                      <ClipboardList size={36} className="text-stone-300" />
                    </div>
                    <p className="text-sm font-semibold text-stone-500">No pending orders</p>
                    <p className="text-xs text-stone-400">Orders you save will appear here</p>
                  </div>
                ) : (
                  myOrders.map((order) => {
                    const pb = order.payment_breakdown || {};
                    const received =
                      (Number(pb.cash) || 0) +
                      (Number(pb.card) || 0) +
                      (Number(pb.upi) || 0);
                    const isLoadingThis = loadingOrderId === order.id;
                    const isCancellingThis = cancellingId === order.id;
                    return (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="border border-amber-200 bg-amber-50/50 rounded-2xl overflow-hidden"
                      >
                        {/* Order Header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-amber-500/10 border-b border-amber-200">
                          <div>
                            <span className="text-xs font-bold text-amber-700 tracking-wide">
                              {order.temp_order_number}
                            </span>
                            <p className="text-[10px] text-stone-400 mt-0.5">
                              {new Date(order.created_at).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          </div>
                          <span className="font-mono font-bold text-amber-700 text-sm">
                            ₹{Number(order.total || order.subtotal || 0).toFixed(2)}
                          </span>
                        </div>

                        {/* Customer Info */}
                        <div className="px-4 py-2 space-y-1">
                          <div className="flex items-center gap-1.5 text-xs text-stone-700">
                            <User size={12} className="text-stone-400" />
                            <span className="font-semibold">{order.customer_name || "—"}</span>
                          </div>
                          {order.customer_phone && (
                            <div className="flex items-center gap-1.5 text-xs text-stone-500">
                              <Phone size={12} className="text-stone-400" />
                              {order.customer_phone}
                            </div>
                          )}
                        </div>

                        {/* Items List */}
                        <div className="px-4 pb-2">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-stone-400 mb-1">
                            Items ({(order.items || []).reduce((s, i) => s + i.quantity, 0)} pcs)
                          </p>
                          <div className="space-y-0.5">
                            {(order.items || []).map((item, idx) => (
                              <div key={idx} className="flex justify-between text-xs">
                                <span className="text-stone-600 truncate max-w-[180px]">
                                  {item.product_name} × {item.quantity}
                                </span>
                                <span className="font-mono text-stone-700 ml-2">
                                  ₹{Number(item.total || 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Payment Summary */}
                        {received > 0 && (
                          <div className="px-4 py-2 border-t border-amber-200 bg-emerald-50/50">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-600 mb-1">
                              Payment Received: ₹{received.toFixed(2)}
                            </p>
                            <div className="flex gap-3 text-[10px] text-stone-500">
                              {Number(pb.cash) > 0 && <span>Cash: ₹{Number(pb.cash).toFixed(2)}</span>}
                              {Number(pb.card) > 0 && <span>Card: ₹{Number(pb.card).toFixed(2)}</span>}
                              {Number(pb.upi) > 0 && <span>UPI: ₹{Number(pb.upi).toFixed(2)}</span>}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="px-4 py-3 border-t border-amber-200 flex gap-2">
                          <button
                            onClick={() => handleLoadIntoCart(order)}
                            disabled={isLoadingThis || isCancellingThis || !!loadingOrderId || !!cancellingId}
                            className="flex-1 flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-bold py-2 rounded-xl transition-colors"
                          >
                            {isLoadingThis ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <Pencil size={13} />
                            )}
                            {isLoadingThis ? "Loading..." : "Edit / Load into Cart"}
                          </button>
                          <button
                            onClick={() => handleCancelOrder(order)}
                            disabled={isLoadingThis || isCancellingThis || !!loadingOrderId || !!cancellingId}
                            className="flex items-center justify-center gap-1.5 bg-rose-50 hover:bg-rose-100 disabled:opacity-50 text-rose-600 border border-rose-200 text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
                            title="Cancel this order"
                          >
                            {isCancellingThis ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <XCircle size={13} />
                            )}
                          </button>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>

              {/* Drawer Footer */}
              {myOrders.length > 0 && !ordersLoading && (
                <div className="px-5 py-3 border-t bg-stone-50 text-center">
                  <p className="text-xs text-stone-400">
                    {myOrders.length} pending order{myOrders.length !== 1 ? "s" : ""} · Showing your orders only
                  </p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-500 rounded-xl">
                <Zap className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-stone-800">
                  Quick POS
                </h1>
                <p className="text-xs md:text-sm text-stone-500">
                  Fast temporary order entry
                </p>
              </div>
            </div>
            {/* My Orders button */}
            <button
              onClick={handleOpenMyOrders}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 font-semibold text-sm rounded-xl transition-all hover:shadow-sm"
            >
              <ClipboardList size={16} />
              <span className="hidden sm:inline">My Orders</span>
              {myOrders.length > 0 && (
                <span className="bg-amber-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {myOrders.length}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT: Product Grid (2/3 width on desktop) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Search & Category Filter */}
            <div className="bg-white rounded-2xl shadow-lg p-4">
              <div className="relative mb-3">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
                />
                <Input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search products..."
                  className="pl-10 h-10"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Category tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setActiveCategory(cat);
                      setCurrentPage(1);
                    }}
                    className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
                      activeCategory === cat
                        ? "bg-amber-500 text-white"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {cat === "all" ? "All" : cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Product Grid */}
            <div className="bg-white rounded-2xl shadow-lg p-4">
              {productsLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div
                      key={i}
                      className="bg-stone-100 rounded-xl overflow-hidden animate-pulse"
                    >
                      <div className="aspect-square bg-stone-200" />
                      <div className="p-2 space-y-2">
                        <div className="h-3 bg-stone-200 rounded w-3/4" />
                        <div className="h-3 bg-stone-200 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Package size={48} className="text-stone-300 mb-3" />
                  <p className="text-stone-400 text-sm">No products found</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                    {filteredProducts.map((product) => {
                      const inCart = cart.find(
                        (i) => i.product_id === product.id,
                      );
                      const isOut = product.stock_quantity === 0;
                      const availableStock =
                        product.stock_quantity - (inCart?.quantity || 0);
                      return (
                        <motion.div
                          key={product.id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          onClick={() => !isOut && addToCart(product)}
                          className={`group relative bg-white border-2 rounded-xl overflow-hidden cursor-pointer transition-all ${
                            isOut
                              ? "border-stone-100 opacity-40 cursor-not-allowed"
                              : inCart
                                ? "border-amber-500 shadow-md"
                                : "border-stone-200 hover:border-amber-300 hover:shadow-sm"
                          }`}
                        >
                          {inCart && (
                            <div className="absolute top-2 left-2 z-10 bg-amber-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-md">
                              {inCart.quantity}
                            </div>
                          )}

                          <div className="aspect-square overflow-hidden bg-stone-50">
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${product.image_url}`}
                              alt={product.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            {isOut && (
                              <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                                <span className="text-xs font-bold text-stone-500 bg-white px-2 py-1 rounded-full border">
                                  Out of Stock
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="p-2">
                            <p className="font-semibold text-xs text-stone-800 line-clamp-2 min-h-[2rem] leading-tight">
                              {product.name}
                            </p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="font-mono font-bold text-amber-600 text-sm">
                                ₹
                                {calculateDiscountedPrice(
                                  product.selling_price,
                                  product.discount,
                                  product.discount_type,
                                )}
                              </span>
                              <span
                                className={`text-xs ${availableStock === 0 ? "text-rose-500" : "text-stone-400"}`}
                              >
                                {availableStock === 0
                                  ? "No Stock"
                                  : `Stock: ${availableStock}`}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <span className="text-xs text-stone-500">
                        Page {currentPage} of {totalPages} · {totalProducts}{" "}
                        products
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="p-2 text-xs rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          «
                        </button>
                        <button
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <span className="px-3 py-2 text-xs font-semibold bg-amber-500 text-white rounded-lg">
                          {currentPage}
                        </span>
                        <button
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ChevronRight size={16} />
                        </button>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className="p-2 text-xs rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          »
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* RIGHT: Cart + Customer + Payment (1/3 width on desktop) */}
          <div className="space-y-4">
            {/* Cart */}
            <div className="bg-white rounded-2xl shadow-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-stone-800">Cart</h2>
                <span className="text-xs text-stone-500">
                  {cart.reduce((s, i) => s + i.quantity, 0)} items
                </span>
              </div>

              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                <AnimatePresence>
                  {cart.map((item) => (
                    <motion.div
                      key={item.product_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-xs text-stone-800 truncate">
                          {item.product_name}
                        </p>
                        <p className="text-xs text-stone-500">
                          ₹{item.price.toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => updateQuantity(item.product_id, -1)}
                          className="w-6 h-6 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center"
                        >
                          <Minus size={10} />
                        </button>
                        <span className="w-6 text-center font-mono font-bold text-xs">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product_id, 1)}
                          className="w-6 h-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center"
                        >
                          <Plus size={10} />
                        </button>
                      </div>
                      <span className="font-mono font-bold text-xs min-w-[50px] text-right">
                        ₹{item.total.toFixed(2)}
                      </span>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="text-rose-500 hover:text-rose-700"
                      >
                        <Trash2 size={14} />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {cart.length === 0 && (
                <div className="text-center py-8 text-stone-400">
                  <Package size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Add products to cart</p>
                </div>
              )}

              <div className="border-t pt-3">
                <div className="flex justify-between text-base font-bold">
                  <span>Subtotal</span>
                  <span className="text-amber-600">₹{subtotal.toFixed(2)}</span>
                </div>
                {appliedCredit > 0 && (
                  <>
                    <div className="flex justify-between text-sm text-emerald-600 mt-1">
                      <span>Credit Used</span>
                      <span>−₹{appliedCredit.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-base font-bold mt-1">
                      <span>Temporary Order Total</span>
                      <span className="text-amber-600">₹{amountPayable.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Customer Details */}
            <div className="bg-white rounded-2xl shadow-lg p-4">
              <h2 className="text-base font-bold text-stone-800 mb-3">
                Customer Details
              </h2>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-stone-500 font-semibold block mb-1">
                    Referred by Staff (for commission)
                  </label>
                  <select
                    value={referredByStaffId}
                    onChange={(e) => setReferredByStaffId(e.target.value)}
                    className="w-full h-9 bg-white border border-stone-200 rounded-lg px-3 text-sm text-stone-700 outline-none focus:border-amber-500 transition-colors"
                  >
                    <option value="">— No referral —</option>
                    {staffList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.role})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <User
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
                  />
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Customer Name *"
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                <Input
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="Billing / Shipping Address (optional)"
                  className="h-9 text-sm"
                />
                <Input
                  value={customerGSTIN}
                  onChange={(e) => setCustomerGSTIN(e.target.value.toUpperCase())}
                  placeholder="Customer GSTIN (optional)"
                  className="h-9 text-sm"
                />
                {creditNotes.length > 0 && (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">Available Credit Notes</p>
                    {creditNotes.map((note) => (
                      <button
                        type="button"
                        key={note.id}
                        onClick={() => {
                          setSelectedCreditNote(note);
                          setCreditAmount(String(roundMoney(Math.min(Number(note.remaining_credit || 0), subtotal))));
                        }}
                        className={`w-full flex justify-between rounded-lg border px-2 py-2 text-xs text-left ${selectedCreditNote?.id === note.id ? "border-emerald-500 bg-emerald-100" : "border-emerald-200 bg-white"}`}
                      >
                        <span className="font-semibold">{note.credit_note_number}</span>
                        <span>₹{Number(note.remaining_credit || 0).toFixed(2)} available</span>
                      </button>
                    ))}
                    {selectedCreditNote && (
                      <Input
                        type="number"
                        min="0"
                        max={Math.min(Number(selectedCreditNote.remaining_credit || 0), subtotal)}
                        value={creditAmount}
                        onChange={(e) => {
                          const maxCredit = Math.min(Number(selectedCreditNote.remaining_credit || 0), subtotal);
                          setCreditAmount(String(roundMoney(Math.min(maxCredit, Math.max(0, Number(e.target.value) || 0)))));
                        }}
                        placeholder="Credit amount to use"
                        className="h-8 text-sm"
                      />
                    )}
                    {selectedCreditNote && (
                      <div className="text-xs text-emerald-700">
                        Credit Used: ₹{appliedCredit.toFixed(2)} · {amountPayableToCustomer > 0
                          ? `Amount Payable to Customer: ₹${amountPayableToCustomer.toFixed(2)}`
                          : `Amount Payable: ₹${amountPayable.toFixed(2)}`}
                      </div>
                    )}
                  </div>
                )}
                {creditSourceOrder && selectedCreditNote && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-[10px] text-blue-800 space-y-1">
                    <p className="font-bold uppercase tracking-wide">Original Bill History</p>
                    <p><b>Invoice:</b> {creditSourceOrder.invoice_number} · <b>Total:</b> ₹{Number(creditSourceOrder.total || 0).toFixed(2)}</p>
                    <p><b>Purchased:</b> {(creditSourceOrder.items || []).map((item) => `${item.product_name} × ${item.quantity} Pcs`).join(", ")}</p>
                    <p><b>Returned:</b> {(selectedCreditNote.returned_items || []).map((item) => `${item.product_name} × ${item.quantity} · ₹${Number(item.total || 0).toFixed(2)}`).join(", ")}</p>
                    <p><b>Previous usage:</b> {(selectedCreditNote.usage_history || []).length ? selectedCreditNote.usage_history.map((u) => `${u.order_number || u.order_id} · ₹${Number(u.amount || 0).toFixed(2)}`).join(", ") : "None"}</p>
                  </div>
                )}
                <div className="relative">
                  <Phone
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
                  />
                  <Input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="Phone Number *"
                    className="pl-9 h-9 text-sm"
                  />
                  {lookingUpCustomer && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-amber-600 font-semibold animate-pulse">
                      Checking...
                    </span>
                  )}
                </div>

                {returningCustomerInfo && (
                  <div className="flex items-center justify-between px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                    <span className="font-semibold flex items-center gap-1">
                      <span>✓</span> Returning Customer ({returningCustomerInfo.total_orders} past orders)
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (returningCustomerInfo.customer_name) setCustomerName(returningCustomerInfo.customer_name);
                        if (returningCustomerInfo.customer_address) setCustomerAddress(returningCustomerInfo.customer_address);
                        if (returningCustomerInfo.customer_gstin) setCustomerGSTIN(returningCustomerInfo.customer_gstin);
                      }}
                      className="text-[10px] font-bold text-emerald-700 underline hover:text-emerald-900"
                    >
                      Refill
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg p-4">
              <h2 className="text-base font-bold text-stone-800 mb-3">
                Payment Received
              </h2>
              <div className="space-y-2">
                {[
                  { key: "cash", label: "Cash", icon: Banknote },
                  { key: "card", label: "Card", icon: CreditCard },
                  { key: "upi", label: "UPI", icon: Smartphone },
                ].map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center gap-2">
                    <Icon size={16} className="text-stone-400" />
                    <span className="text-xs font-medium text-stone-600 w-12">
                      {label}
                    </span>
                    <div className="relative flex-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 text-xs">
                        ₹
                      </span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={paymentBreakdown[key]}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "" || /^\d*\.?\d*$/.test(v)) {
                            setPaymentBreakdown((prev) => ({
                              ...prev,
                              [key]: v,
                            }));
                          }
                        }}
                        placeholder="0.00"
                        className="pl-6 h-8 font-mono text-sm"
                      />
                    </div>
                  </div>
                ))}
                <div className="border-t pt-2 flex justify-between font-semibold text-sm">
                  <span>Total Received</span>
                  <span className="text-emerald-600">
                    ₹{amountReceived.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveTemporary}
              disabled={saving || cart.length === 0 || !customerName.trim()}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-base rounded-xl shadow-lg disabled:opacity-50"
            >
              {saving ? (
                <>Processing...</>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Save Temporary Order
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickPOS;
